import * as React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import CategoryPage from './CategoryPage';
import { TemplatePage } from './TemplatePage';
import { ScrollToTop } from './components/ScrollToTop';
import { logConfig, WebsiteLogger } from '@prezi/client-logger-module';

import { isDevelopmentEnv } from './utils';

export interface CategoryData {
    slug: string;
    name: string;
    short_name: string;
    description: string;
    page_title: string;
    meta_description: string;
    meta_tags: string;
    element_id?: string;
}

export interface VideoMetadata {
    fps: number;
    height: number;
    width: number;
    steps: number;
    keyframes: Array<number>;
}

export interface TemplateData {
    slug: string;
    name: string;
    description: string;
    categories: Array<string>;
    thumbnail_url: string;
    title: string;
    template_key: string;
    video_url?: string;
    video_metadata?: VideoMetadata;
    element_id?: string;
}

export interface AppProps {
    categories: Array<CategoryData>;
    templates: Array<TemplateData>;
    isAuthenticated: boolean;
    preloadTemplatePreviewVideos: boolean;
    categoryString: string;
    useThisTemplateCTA: string;
    getStartedCTA: string;
    relatedTemplatesString: string;
    usePARF: boolean;
}

interface RedirectMatchParams {
    url: string;
}

export const CATEGORY_ALL_SLUG = '/';
const basePathRegex = '/([a-z][a-z])?/(cms)?/:main/';

export class App extends React.Component<AppProps, any> {
    categories: Array<CategoryData>;
    templates: Array<TemplateData>;
    categoryLookup: any;
    templateLookup: any;
    errors: Array<string>;
    categoryPath: string;
    templatePath: string;
    defaultPath: string;
    categoryString: string;
    useThisTemplateCTA: string;
    getStartedCTA: string;
    relatedTemplatesString: string;

    constructor(props: AppProps) {
        super(props);
        this.categories = this.props.categories;
        this.templates = this.props.templates;
        this.categoryString = this.props.categoryString;
        this.useThisTemplateCTA = this.props.useThisTemplateCTA;
        this.getStartedCTA = this.props.getStartedCTA;
        this.relatedTemplatesString = this.props.relatedTemplatesString;

        this.errors = [];
        this.categoryLookup = {};
        this.categories.forEach(category => {
            if (this.categoryLookup[category.slug]) {
                this.errors.push(`Duplicate category [${category.slug}] ${category.name}`);
            } else {
                this.categoryLookup[category.slug] = category;
            }
        });

        this.templateLookup = {};
        this.templates.forEach(template => {
            if (this.templateLookup[template.slug]) {
                this.errors.push(`Duplicate template [${template.slug}] ${template.name}`);
            } else if (this.categoryLookup[template.slug]) {
                this.errors.push(`Template slug cannot match a category slug.  Template: [${template.slug}] ${template.name}`);
            } else {
                this.templateLookup[template.slug] = template;
            }
        });

        const categoryRegex: string = this.generateSlugRegex(this.categories.map(category => category.slug));
        const templateRegex: string = this.generateSlugRegex(this.templates.map(template => template.slug));

        this.categoryPath = basePathRegex + categoryRegex;
        this.templatePath = basePathRegex + templateRegex;
        this.defaultPath = basePathRegex + ':extraenous?';

        if (isDevelopmentEnv()) {
            WebsiteLogger.log = console.log;
        } else {
            WebsiteLogger.initLogger(logConfig, '//log.prezi.com/log/json');
        }
    }

    generateSlugRegex(slugs: Array<string>): string {
        return '(' + slugs.join('|') + ')';
    }

    buildBasePath(matchParams: {[group: string]: string}): string {
        const lang = matchParams['0'];
        const cms = matchParams['1'];
        const main = matchParams['main'];

        let basePath = '/';

        if (lang) { basePath += lang + '/'; }
        if (cms) { basePath += cms + '/'; }
        basePath += main + '/';

        return basePath;
    }

    render () {
        let errorDiv = <div />;

        if (this.errors.length) {
            errorDiv = <div style={ {backgroundColor: 'red'} } className="row">
                <h1>Error in Data</h1>
                <ul>
                { this.errors.map(error => <li key={ 'error' }> { error } </li>) }
                </ul>
            </div>;
        }

        return (
            <ScrollToTop>
                { errorDiv }
                <Switch>
                    <Route exact={ true } strict={ true } path="/:url*"
                           render={ (props: RouteComponentProps<RedirectMatchParams>) => <Redirect to={ `${props.location.pathname}/` } /> }
                    />
                    <Route path={ this.categoryPath } render={ ({match}) =>
                        <CategoryPage
                            currentCategory={ this.categoryLookup[match.params[2]] }
                            templates={ this.templates.filter(template =>
                                template.categories.indexOf(match.params[2]) !== -1,
                            ) }
                            categories={ this.categories }
                            basePath={ this.buildBasePath(match.params) }
                            categoryString={ this.categoryString }
                        />
                    } />
                    <Route path={ this.templatePath } render={ ({match}) =>
                        <TemplatePage
                            currentTemplate={ this.templateLookup[match.params[2]] }
                            templates={ this.templates }
                            categories={ this.categories }
                            basePath={ this.buildBasePath(match.params) }
                            isAuthenticated={ this.props.isAuthenticated }
                            preloadPreviewVideos={ this.props.preloadTemplatePreviewVideos }
                            categoryString={ this.categoryString }
                            useThisTemplateCTA={ this.useThisTemplateCTA }
                            getStartedCTA={ this.getStartedCTA }
                            usePARF={ this.props.usePARF }
                            relatedTemplatesString={ this.relatedTemplatesString }
                        />
                    } />
                    <Route path={ this.defaultPath } render={ ({match}) => {
                        if (match.params.extraenous) {
                            return <Redirect to={ this.buildBasePath(match.params) } />;
                        }
                        return (
                        <CategoryPage
                            currentCategory={ this.categoryLookup[CATEGORY_ALL_SLUG] }
                            templates={ this.templates }
                            categories={ this.categories }
                            basePath={ this.buildBasePath(match.params) }
                            categoryString={ this.categoryString }
                        />
                    ); } } />
                </Switch>
            </ScrollToTop>
        );
    }
}



// WEBPACK FOOTER //
// ./assets/js/App.tsx